import React, { useMemo } from 'react'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import Pane from 'components/Common/Pane'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { isLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import styled from 'styled-components'
import cn from 'clsx'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import { INCLUSIONS_TITLE_LUXPLUS } from 'constants/inclusions'
import { LabelCountProvider } from 'contexts/LabelCountContext'
import useOfferList from 'hooks/Offers/useOfferList'

const StyledPane = styled(Pane)`
  display: flex;
  flex-direction: column;

  &.free-preview-order {
    #exclusive_offers {
      order: 1;
    }

    #early_access {
      order: 2;
    }

    #discounted_flash {
      order: 3;
    }

    #hotel_inclusions {
      order: 4;
    }

    #discounted_tours {
      order: 5;
    }
  }

  &.single-early-access-order {
    #early_access {
      order: 6;
    }
  }
`

interface MappedProps {
  canViewLuxPlusBenefits: boolean;
  isLuxPlusToursEnabled: boolean;
}

interface Props extends MappedProps {
  analyticsPage: string;
}

function LuxPlusOfferCarousels(props: Props) {
  const { analyticsPage, canViewLuxPlusBenefits, isLuxPlusToursEnabled } = props

  const { showFreePreviewCarouselSortOrder } = useLuxPlusFreePreviewUtils()

  const memberTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Member'))
  const memberPriceFilter = useMemo<App.OfferListFilters>(() => ({
    offerTypes: ['hotel', 'tactical_ao_hotel'],
    luxPlusFeatures: ['Member prices'],
    destinations: [Places.Anywhere.id],
    sortBy: 'luxPlusList',
  }), [])

  const earlyAccessTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Early access'))
  const earlyAccessSearchFilter = useMemo<App.OfferListFilters>(() => ({
    offerTypes: ['hotel', 'tactical_ao_hotel', 'tour_v2'],
    luxPlusFeatures: ['Early access'],
    destinations: [Places.Anywhere.id],
    sortBy: 'luxPlusList',
  }), [])
  const earlyAccessCarouselFilter = useMemo<App.OfferListFilters>(() => ({
    ...earlyAccessSearchFilter,
    sortBy: 'vertical-luxplus',
  }), [earlyAccessSearchFilter])
  const earlyAccessOfferList = useOfferList(earlyAccessCarouselFilter)

  const bonusInclusionsTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Bonus inclusions'))
  const bonusInclusionFilter = useMemo<App.OfferListFilters>(() => ({
    offerTypes: ['hotel', 'tactical_ao_hotel'],
    luxPlusFeatures: ['Bonus inclusions'],
    destinations: [Places.Anywhere.id],
    sortBy: 'luxPlusList',
  }), [])

  const memberOnlyTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Member only'))
  const memberOnlyFilter = useMemo<App.OfferListFilters>(() => ({
    offerTypes: ['hotel', 'tactical_ao_hotel'],
    luxPlusFeatures: ['Member exclusive'],
    destinations: [Places.Anywhere.id],
    sortBy: 'luxPlusList',
    scheduleVisibilities: ['luxPlusExclusiveVisible'],
  }), [])

  const tourMemberPricesTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Tour member prices'))
  const tourMemberPricesFilter = useMemo<App.OfferListFilters>(() => ({
    offerTypes: ['tour_v2'],
    luxPlusFeatures: ['Member prices'],
    destinations: [Places.Anywhere.id],
    sortBy: 'homepage',
  }), [])

  return <LabelCountProvider maxLabels={2}>
    <StyledPane className={cn({
      'free-preview-order': showFreePreviewCarouselSortOrder,
      'single-early-access-order': earlyAccessOfferList.offerIds.length <= 1,
    })}>
      <OfferListEventsProvider tracking={memberTracking}>
        <OfferCardCarouselSection
          title="Huge savings on our best hotel offers"
          subtitle="Save hundreds every year with additional discounts on our best Limited Time LUX Exclusive offers"
          filters={memberPriceFilter}
          allLink={`/search?${getSearchQueryFromFilter(memberPriceFilter)}`}
          id="discounted_flash"
          hideWhenEmpty
        />
      </OfferListEventsProvider>
      <OfferListEventsProvider tracking={earlyAccessTracking}>
        <OfferCardCarouselSection
          title="Early access to our bestselling offers"
          subtitle="Take your pick with early access to our Limited Time LUX Exclusive and Exclusive Tour offers before the general public"
          filters={earlyAccessCarouselFilter}
          allLink={`/search?${getSearchQueryFromFilter(earlyAccessSearchFilter)}`}
          id="early_access"
          hideWhenEmpty
        />
      </OfferListEventsProvider>
      <OfferListEventsProvider tracking={bonusInclusionsTracking}>
        <OfferCardCarouselSection
          title={`Unlock ${INCLUSIONS_TITLE_LUXPLUS} at select hotels`}
          subtitle="Make the most of hotel credit, daily breakfast and a complimentary room upgrade*"
          filters={bonusInclusionFilter}
          allLink={`/search?${getSearchQueryFromFilter(bonusInclusionFilter)}`}
          id="hotel_inclusions"
          hideWhenEmpty
        />
      </OfferListEventsProvider>
      <OfferListEventsProvider tracking={memberOnlyTracking}>
        <OfferCardCarouselSection
          tileStyle={canViewLuxPlusBenefits ? 'card' : 'locked-card'}
          title="Hidden offers, just for members"
          subtitle={`Unlock hidden hotel offers available exclusively for ${LUXURY_PLUS.PROGRAM_NAME} members. These escapes aren't available anywhere else`}
          filters={memberOnlyFilter}
          allLink={canViewLuxPlusBenefits ? `/search?${getSearchQueryFromFilter(memberOnlyFilter)}` : undefined}
          id="exclusive_offers"
          hideWhenEmpty
        />
      </OfferListEventsProvider>
      {isLuxPlusToursEnabled && <OfferListEventsProvider tracking={tourMemberPricesTracking}>
        <OfferCardCarouselSection
          title="Save more on Luxury Escapes Tours"
          subtitle="Explore the world and save hundreds on select tours"
          filters={tourMemberPricesFilter}
          allLink={`/search/tours?${getSearchQueryFromFilter(tourMemberPricesFilter)}`}
          id="discounted_tours"
          hideWhenEmpty
        />
      </OfferListEventsProvider>}
    </StyledPane>
  </LabelCountProvider>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: checkCanViewLuxPlusBenefits(state),
  isLuxPlusToursEnabled: isLuxPlusToursEnabled(state),
})

export default connect(mapStateToProps)(LuxPlusOfferCarousels)
